import { BLOCKS, INLINES, Inline, Block, Text } from '@contentful/rich-text-types'
import * as CFRichTextTypes from '@contentful/rich-text-types'
import { isTypeComponentTextInput } from '../../../../lib/types/content-types'
import { ParaphrasingPromptType, ParaphrasingPromptTypesToData } from '../utils'

/**
 * Builds the prompt to be sent to the llm by injecting vars into the Contentful-specified llm prompt.
 *
 * Terminology:
 *   var: A value to be injected into LLM prompt. Currently, we only support user input text. Context-based var injection like logged in state, user name, etc. is not supported.
 *   clause: A section of the LLM prompt that is associated with a var. If the var is not present, the entire clause will be omitted from the prompt.
 */
export const fillOutLlmPrompt = (
  prompt: CFRichTextTypes.Document,
  formData: FormData,
  paraphrasingPrompt?: ParaphrasingPromptType
): string => {
  // Transform from contentful tree into a string with placeholders
  const promptAsString = prompt.content.map((node) => renderContentfulNodeToString(node)).join('')

  // Transform from string with placeholders into an internal tree with nodes representing variable clauses
  let clauses = parseVariableClauses(promptAsString)

  if (paraphrasingPrompt !== undefined) {
    // Find the Clause variable name and replace it (assume there is only one variable)
    const clauseWithVar = clauses.find((clause) => clause.kind === 'clause') as ClauseNode | undefined

    if (clauseWithVar) {
      clauses = ParaphrasingPromptTypesToData[paraphrasingPrompt].getClauses(clauseWithVar.varName)
    } else {
      console.error('Could not find variable name in clauses, please check contentful')
    }
  }

  return injectVariables(clauses, formData)
}

const isBlock = (node: Block | Inline | Text): node is Block => {
  return Object.values(BLOCKS).includes(node.nodeType as BLOCKS)
}

const renderContentfulNodeToString = (node: Block | Inline | Text): string => {
  if (node.nodeType === 'text') {
    return node.value
  } else if (isBlock(node)) {
    return '\n' + node.content.map((node) => renderContentfulNodeToString(node)).join('') + '\n'
  } else if (node.nodeType === INLINES.EMBEDDED_ENTRY) {
    return renderEmbeddedInlineEntry(node as Inline)
  }
  return ''
}

const renderEmbeddedInlineEntry = (node: Inline): string => {
  if (isTypeComponentTextInput(node.data.target)) {
    const fields = node.data.target.fields
    const { name } = fields
    return `{${name}}`
  }

  return ''
}

// Note that the following tree/node structure is separate from Contentful's rich text tree and nodes.
export type TextNode = {
  kind: 'text'
  value: string
}

export type ClauseNode = {
  kind: 'clause'
  content: (TextNode | VarNode)[]
  varName: string
}

type VarNode = {
  kind: 'var'
  varName: string
}

/**
 * Traverses text by char and transforms into a tree ready for var injection. Here's an example to help clarify the code.
 *
 * Text input:
 * Write an engaging cover letter. {Include details from my resume: {resume}}
 *                                 ^                                ^      ^^
 *                                 1                                2      34
 * 1: Clause start brace
 * 2: Var name start brace
 * 3: Var name end brace
 * 4: Clause end brace
 */
const parseVariableClauses = (text: string): (TextNode | ClauseNode)[] => {
  let i = 0
  let clauseStartBrace = text.indexOf('{')
  const nodes: (TextNode | ClauseNode)[] = []

  let iterations = 0
  while (clauseStartBrace >= 0) {
    ++iterations
    if (iterations > 100) {
      throw new Error('Error parsing LLM prompt: infinite loop')
    }
    if (clauseStartBrace > i) {
      nodes.push({ kind: 'text', value: text.slice(i, clauseStartBrace) })
    }
    const { node, clauseEndBrace, nextClauseStartBrace } = parseClause(text, clauseStartBrace + 1)
    nodes.push(node)

    i = clauseEndBrace + 1
    clauseStartBrace = nextClauseStartBrace
  }
  if (i < text.length) {
    nodes.push({ kind: 'text', value: text.slice(i) })
  }
  return nodes
}

const parseClause = (
  text: string,
  start = 0
): { node: ClauseNode; clauseEndBrace: number; nextClauseStartBrace: number } => {
  const nodes: (TextNode | VarNode)[] = []

  let varNameStartBrace = text.indexOf('{', start)
  const varNameEndBrace = text.indexOf('}', start)
  let clauseEndBrace = text.indexOf('}', varNameEndBrace + 1)
  if (varNameStartBrace < 0 || varNameEndBrace < varNameStartBrace) {
    // A clause with no braces in it means this is just a variable without a surrounding clause
    varNameStartBrace = start - 1
    clauseEndBrace = varNameEndBrace
  }

  const nextClauseStartBrace = text.indexOf('{', varNameStartBrace + 1)
  if (nextClauseStartBrace >= 0 && nextClauseStartBrace < clauseEndBrace) {
    throw new Error('Error parsing LLM prompt: missing closing brace.')
  }

  if (varNameStartBrace > start) {
    nodes.push({ kind: 'text', value: text.slice(start, varNameStartBrace) })
  }
  const varName = text.slice(varNameStartBrace + 1, varNameEndBrace)
  nodes.push({ kind: 'var', varName })
  if (clauseEndBrace > varNameEndBrace + 1) {
    nodes.push({ kind: 'text', value: text.slice(clauseEndBrace + 1, varNameEndBrace) })
  }
  return { node: { kind: 'clause', content: nodes, varName }, clauseEndBrace, nextClauseStartBrace }
}

const injectVariables = (clauses: (TextNode | ClauseNode)[], formData: FormData): string => {
  const clauseStrings: string[] = []

  for (const clause of clauses) {
    if (clause.kind === 'text') {
      clauseStrings.push(clause.value)
    } else {
      const key = clause.varName
      const formValue = formData.get(key)

      if (typeof formValue === 'string' && formValue.trim()) {
        for (const subClause of clause.content) {
          if (subClause.kind === 'text') {
            clauseStrings.push(subClause.value)
          } else {
            clauseStrings.push(formValue)
          }
        }
      }
    }
  }
  return clauseStrings.join('')
}
